import ImageIcon from '@mui/icons-material/Image';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { Box, Card, CardContent, CardHeader, IconButton } from '@mui/material';
import BoardCanvasWithOverlays from 'boardCanvas/BoardCanvasWithOverlays';
import { ALLOW_WHEN_POPOVER_OPEN } from 'Constants';
import { useRef, useState } from 'react';
import {
  BoardType,
  Material,
  Renderable,
  RenderableMetadata,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { useUpdateBoard } from 'state/recoilBoardActions';
import { getDisplayLengthInUnits } from 'utils/utils';

function EditorPreviewCard<T extends BoardType>(props: {
  boardInstance: BoardBase<T>;
  onSelectionChange: (selection: null | RenderableMetadata<T>) => void;
  selection: null | RenderableMetadata<T>;
}) {
  const { boardInstance, onSelectionChange, selection } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const updateBoard = useUpdateBoard<T>();

  const [rotation, setRotation] = useState(0);

  const onSelectedFragmentMaterialChange = (
    material: Material,
    selectedRenderable: Renderable<T>
  ) => {
    updateBoard(
      boardInstance.handleSelectedMaterialChange(selectedRenderable, material)
    );
  };

  const onSelectedFragmentSizeChange = (
    size: number,
    dimension: 'height' | 'width',
    selectedRenderable: Renderable<T>
  ) => {
    updateBoard(
      boardInstance.handleSelectedSizeChange(
        selectedRenderable,
        size,
        dimension
      )
    );
  };

  const rotateLeft = () => setRotation(rotation - 90);
  const rotateRight = () => setRotation(rotation + 90);
  
  const downloadImage = () => {
    const canvas = canvasRef.current

    if (canvas == null) return

    const el = document.createElement('a')

    el.href = canvas.toDataURL()
    el.download = 'cutting-board-designer-preview.jpg'
    
    el.click()
  }

  const { width, height } = boardInstance
    ? boardInstance.getDimensions()
    : { height: 0, width: 0 };

  const displayLength = getDisplayLengthInUnits(
    boardInstance?.units ?? Units.IN
  );

  return (
    <Card>
      <CardHeader
        action={
          <Box>
            <IconButton
              aria-label="download image"
              onClick={downloadImage}
              className={ALLOW_WHEN_POPOVER_OPEN}
            >
              <ImageIcon></ImageIcon>
            </IconButton>
            <IconButton
              aria-label="rotate left"
              onClick={rotateLeft}
              className={ALLOW_WHEN_POPOVER_OPEN}
            >
              <RotateLeftIcon></RotateLeftIcon>
            </IconButton>
            <IconButton
              aria-label="rotate right"
              onClick={rotateRight}
              className={ALLOW_WHEN_POPOVER_OPEN}
            >
              <RotateRightIcon></RotateRightIcon>
            </IconButton>
          </Box>
        }
        title="Preview"
        subheader={`${displayLength(width)} x ${displayLength(height)}`}
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <BoardCanvasWithOverlays
            boardInstance={boardInstance}
            canvasRef={canvasRef}
            rotation={rotation}
            onDrag={() => {}}
            onDragEnd={() => {}}
            onDragStart={() => {}}
            onSelectionChange={onSelectionChange}
            onSelectedFragmentMaterialChange={onSelectedFragmentMaterialChange}
            onSelectedFragmentSizeChange={onSelectedFragmentSizeChange}
            selection={selection}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default EditorPreviewCard;
